import { useEffect, useState } from 'react';
import { Button, Callout, Text } from '@tremor/react';
import { fetchBusy, verbLabel } from '../api/activity';
import { t } from '../lib/i18n';

interface Props {
  /** The raw error message from the catch block. We translate the known
   *  "503" daemon-offline string into a friendlier line; everything else
   *  passes through. */
  message: string;
  /** Called when the user clicks Reintentar. Usually the same loader the
   *  page's useEffect calls. */
  onRetry: () => void;
  /** Optional title override; defaults to t.dashboard.errorTitle so every
   *  page surfaces the same headline. */
  title?: string;
}

/** True for client-side aborts/timeouts (AbortController firing, fetch
 *  timeouts) — the "signal is aborted without reason" family that used to
 *  reach the user verbatim and left them lost. */
function isAbortMessage(m: string): boolean {
  return /abort|timed out|timeout/i.test(m);
}

/**
 * Standard error-with-retry card used across every page that loads data.
 * Replaces the ad-hoc red div + Text("loading") pattern with something
 * recoverable in one click. Matches the rose Callout style introduced in
 * the dashboard pass so the visual language stays consistent.
 *
 * Abort/timeout errors get two upgrades: the raw browser message is replaced
 * with a human explanation (the server finishes the query in the background —
 * a retry usually serves instantly from cache), and /api/activity/busy is
 * consulted so a user stalled behind SOMEONE ELSE's heavy query is told
 * exactly that instead of being left guessing.
 */
export default function ErrorRetry({ message, onRetry, title }: Props) {
  const aborted = isAbortMessage(message);
  const [busyLine, setBusyLine] = useState<string | null>(null);

  useEffect(() => {
    if (!aborted) return;
    let alive = true;
    fetchBusy()
      .then((b) => {
        if (alive && b.busy) {
          setBusyLine(
            t.errors.serverBusy
              .replace('{verb}', verbLabel(b.verb))
              .replace('{secs}', String(Math.round((b.running_ms ?? 0) / 1000))),
          );
        }
      })
      .catch(() => { /* busy signal is best-effort */ });
    return () => { alive = false; };
  }, [message, aborted]);

  const display = message.includes('503')
    ? t.errors.daemonOffline
    : aborted
      ? t.errors.timedOut
      : message;
  return (
    <Callout title={title ?? t.dashboard.errorTitle} color="rose">
      <div className="space-y-2">
        <Text>{display}</Text>
        {busyLine && <Text>{busyLine}</Text>}
        <Button size="xs" variant="primary" onClick={onRetry}>
          {t.dashboard.retry}
        </Button>
      </div>
    </Callout>
  );
}
